<template>
  <section class="cross-fabric">
    <el-checkbox v-model="enabled" :disabled="disabled || busy">针对面料材质</el-checkbox>
    <div v-for="(method, index) in methods" :key="method.mode" class="fabric-method">
      <el-radio v-model="mode" :label="method.mode" :disabled="disabled || busy">方式{{ index + 1 }}：</el-radio>
      <div class="fabric-fields">
        <template v-if="method.mode === 'set'">
          <el-checkbox v-model="forms.set.edit_fabric" :disabled="disabled || busy">修改面料材质</el-checkbox>
          <fabric-composition-input v-model="forms.set.composition" :disabled="disabled || busy" />
          <div class="fabric-condition">
            <el-checkbox v-model="forms.set.onlyContains" :disabled="disabled || busy">仅针对面料材质含</el-checkbox>
            <el-input
              v-model="forms.set.contains"
              placeholder="多个成分用中英文逗号分隔"
              :disabled="disabled || busy" />
            <span>才修改</span>
          </div>
          <small>任一成分精确命中才修改；两个条件同时勾选时须同时满足。</small>
          <el-checkbox v-model="forms.set.onlyEmpty" :disabled="disabled || busy"
            >仅针对面料材质成分和比例都为空的才修改</el-checkbox
          >
        </template>
        <template v-else-if="method.mode === 'replace'">
          <el-checkbox v-model="forms.replace.edit_fabric" :disabled="disabled || busy">针对面料材质成分</el-checkbox>
          <div class="fabric-replace">
            <el-input v-model="forms.replace.from" placeholder="原成分，例如其他" :disabled="disabled || busy" />
            <span>修改为</span>
            <el-input v-model="forms.replace.to" placeholder="目标成分，例如聚酯纤维" :disabled="disabled || busy" />
            <span>成分比例保持不变</span>
          </div>
        </template>
        <template v-else-if="method.mode === 'set_image'">
          <span>针对面料材质为：</span>
          <fabric-composition-input v-model="forms.set_image.composition" :disabled="disabled || busy" />
          <small>完整成分与比例相同的商品，统一设置水洗标/吊牌图。</small>
        </template>
        <fabric-image-input
          v-if="method.mode !== 'delete_image'"
          v-model="forms[method.mode].image"
          :shops="shops"
          compact
          :replace-only="method.mode === 'set_image'"
          :disabled="disabled || busy"
          @busy="imageBusy" />
        <span v-else>删除水洗标/吊牌图（若图片为必填，则不进行修改）</span>
      </div>
    </div>
    <p class="fabric-tip">
      面料按各商品类目校验，比例合计须为100%；图片经平台审核后才提交。含面料修改的批次暂不支持恢复。
    </p>
  </section>
</template>

<script>
import FabricCompositionInput from './FabricCompositionInput.vue'
import FabricImageInput from './FabricImageInput.vue'

const image = (action = 'keep') => ({ action, source_kind: 'url', source_ref: '', skip_existing: true })
export default {
  components: { FabricCompositionInput, FabricImageInput },
  props: { disabled: Boolean, shops: { type: Array, default: () => [] } },
  data: () => ({
    enabled: false,
    mode: 'set',
    busy: false,
    methods: [{ mode: 'set' }, { mode: 'replace' }, { mode: 'set_image' }, { mode: 'delete_image' }],
    forms: {
      set: {
        edit_fabric: true,
        composition: [{ name: '', percent: '' }],
        onlyContains: false,
        contains: '',
        onlyEmpty: false,
        image: image()
      },
      replace: { edit_fabric: true, from: '', to: '', image: image() },
      set_image: { composition: [{ name: '', percent: '' }], image: image('replace') }
    }
  }),
  computed: {
    validationError() {
      try {
        this.validate()
        return ''
      } catch (error) {
        return error.message
      }
    },
    rule() {
      if (!this.enabled) return null
      if (this.mode === 'delete_image') return { mode: this.mode }
      const form = this.forms[this.mode]
      const rule = { mode: this.mode, image: { action: form.image.action } }
      if (form.image.action === 'replace') {
        rule.image = Object.fromEntries(Object.entries(form.image).filter(([, value]) => value !== undefined))
      }
      if (this.mode === 'set_image')
        return { ...rule, edit_fabric: false, match_composition: form.composition.map(this.composition) }
      rule.edit_fabric = form.edit_fabric
      if (form.edit_fabric && this.mode === 'set') {
        rule.composition = form.composition.map(this.composition)
        rule.conditions = {
          contains_any: form.onlyContains
            ? [
                ...new Set(
                  form.contains
                    .split(/[,，]/)
                    .map((value) => value.trim())
                    .filter(Boolean)
                )
              ]
            : [],
          only_empty: form.onlyEmpty
        }
      }
      if (form.edit_fabric && this.mode === 'replace') rule.replace = { from: form.from.trim(), to: form.to.trim() }
      return rule
    }
  },
  watch: {
    validationError: {
      immediate: true,
      handler(value) {
        this.$emit('validation', value)
      }
    },
    rule: {
      deep: true,
      handler(value) {
        this.$emit('input', value)
      }
    }
  },
  methods: {
    composition(row) {
      return { name: row.name.trim(), percent: row.percent.trim() }
    },
    imageBusy(value) {
      this.busy = value
      this.$emit('busy', value)
    },
    validate() {
      const rule = this.rule
      if (!rule) return
      if (this.busy) throw new Error('请等待图片处理完成')
      const rows = rule.composition || rule.match_composition
      if (rows) {
        if (
          !rows.length ||
          rows.some(
            (row) =>
              !row.name || !/^(?:0|[1-9][0-9]{0,2})(?:\.[0-9]{1,2})?$/.test(row.percent) || Number(row.percent) <= 0
          )
        )
          throw new Error('请完整填写面料成分和有效比例')
        if (new Set(rows.map((row) => row.name)).size !== rows.length) throw new Error('面料成分不能重复')
        const total = rows.reduce((sum, row) => {
          const [whole, fraction = ''] = row.percent.split('.')
          return sum + Number(whole) * 100 + Number(fraction.padEnd(2, '0'))
        }, 0)
        if (total !== 10000) throw new Error('面料成分比例合计必须为100%')
      }
      if (rule.replace && (!rule.replace.from || !rule.replace.to)) throw new Error('请填写替换前后面料成分')
      if (
        this.mode === 'set' &&
        this.forms.set.edit_fabric &&
        this.forms.set.onlyContains &&
        !rule.conditions.contains_any.length
      )
        throw new Error('请填写需要匹配的面料成分')
      if (rule.image?.action === 'replace' && !rule.image.source_ref) throw new Error('请选择水洗标/吊牌图片')
      if (['set', 'replace'].includes(this.mode) && !rule.edit_fabric && rule.image.action === 'keep')
        throw new Error('请勾选面料或图片修改')
    }
  }
}
</script>

<style scoped>
.cross-fabric {
  margin-top: 18px;
  font-size: 12px;
}
.fabric-method {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin: 18px 0 24px 28px;
}
.fabric-method > .el-radio {
  flex: 0 0 80px;
  margin: 5px 0 0;
}
.fabric-fields {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 9px;
  min-width: 0;
}
.fabric-condition,
.fabric-replace {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  max-width: 100%;
}
.fabric-condition > .el-input {
  width: 225px;
  max-width: 100%;
}
.fabric-replace > .el-input {
  width: 160px;
  max-width: 100%;
}
.fabric-fields small,
.fabric-tip {
  color: #999;
  font-size: 11px;
  line-height: 20px;
}
.cross-fabric ::v-deep .el-checkbox__label,
.cross-fabric ::v-deep .el-radio__label {
  font-size: 12px;
  white-space: normal;
}
.cross-fabric ::v-deep .el-radio__label {
  white-space: nowrap;
}
.cross-fabric ::v-deep .el-input__inner {
  height: 30px;
  line-height: 30px;
  font-size: 11px;
}
.cross-fabric ::v-deep .fabric-line > .el-autocomplete {
  width: 150px;
}
.cross-fabric ::v-deep .fabric-line > .el-input {
  width: 90px;
}
@media (max-width: 600px) {
  .fabric-method {
    margin-left: 0;
    gap: 6px;
  }
  .fabric-method > .el-radio {
    flex-basis: 72px;
  }
}
</style>
